import React from 'react';
import PropTypes from 'prop-types';
import uuid from 'uuid';
import {
  Button,
  InputGroup,
  Tooltip,
  OverlayTrigger,
  Form,
} from 'react-bootstrap';
import { Button as CanaveralButton } from '@storable/canaveral-components';
import AsyncFeedback from 'components/AsyncFeedback/AsyncFeedback';
import Authorization from 'containers/Authorization';
import { BUILDER } from 'shared/abilities';
import UnauthorizedBanner from '../UnauthorizedBanner';
import Icon from '../../../../../../modules/react/components/stateless/Icon';
import style from './HostnameSettings.scss';

// Helper function for creating state from the props
const prepareState = function ({ hostnames }) {
  const state = {
    touchedHostnames: [],
    deletesToProcess: [],
  };

  if (hostnames && hostnames.length > 0) {
    // Create new objects to prevent mutating redux state
    state.hostnames = hostnames.map(obj => ({ ...obj }));
  } else {
    // Otherwise we start with a sensible default hostname
    const id = uuid.v4();
    state.hostnames = [{
      id,
      hostname: '',
      primary: true,
      redirects: [],
    }];
    state.touchedHostnames.push(id);
  }

  return state;
};

export default class HostnameSettings extends React.Component {
  static propTypes = {
    hostnames: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      hostname: PropTypes.string.isRequired,
      primary: PropTypes.bool.isRequired,
    })),
    onSave: PropTypes.func.isRequired,
    isSaving: PropTypes.bool.isRequired,
    error: PropTypes.array,
  };

  static contextTypes = {
    setTab: PropTypes.func.isRequired,
  };

  state = prepareState(this.props);

  UNSAFE_componentWillMount() {
    this.context.setTab('settings/domains');
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.hostnames !== nextProps.hostnames) {
      this.setState(prepareState(nextProps));
    }
  }

  handleChange = id => ({ target }) => {
    this.touchHostname(id);

    this.setState(({ hostnames }) => ({
      hostnames: hostnames.map((hostname) => {
        if (id === hostname.id) {
          return {
            ...hostname,
            hostname: target.value.toLowerCase(),
          };
        }

        return hostname;
      }),
    }));
  }

  handleDelete = idToDelete => () => {
    this.setState(({ hostnames, deletesToProcess }) => ({
      hostnames: hostnames.filter(({ id }) => id !== idToDelete),
      deletesToProcess: [...deletesToProcess, idToDelete],
    }));
  }

  handleNewSecondary = () => {
    const id = uuid.v4();

    this.setState(({ hostnames: prevHosts }) => ({
      hostnames: [
        ...prevHosts,
        {
          id,
          hostname: '',
          primary: false,
          redirects: [],
        },
      ],
    }));
    this.touchHostname(id);
  };

  handleSubmit = (event) => {
    event.preventDefault();

    const { touchedHostnames, deletesToProcess, hostnames } = this.state;
    const updates = hostnames.filter(({ id }) =>
      touchedHostnames.indexOf(id) !== -1
      && deletesToProcess.indexOf(id) === -1);

    this.props.onSave(updates, deletesToProcess);
  };

  touchHostname = (id) => {
    // Let the state know that this hostname was touched
    if (this.state.touchedHostnames.indexOf(id) === -1) {
      this.setState(({ touchedHostnames }) => ({
        touchedHostnames: [...touchedHostnames, id],
      }));
    }
  };

  render() {
    if (!this.props.hostnames) {
      return <div>Loading...</div>;
    }

    const { isSaving, error } = this.props;
    const { hostnames } = this.state;

    const tooltip = (
      <OverlayTrigger
        placement="right"
        overlay={
          <Tooltip id="domain-help-tooltip">
            <div>
              Redirecting non www to www:
              <p>
                The primary domain needs to include www.<br />
                 Example: www.myexamplestorage.com
              </p>

              <p>Set up a secondary domain with the non www version of the primary domain.<br />
                 Example: myexamplestorage.com
              </p>

              Non www will now redirect www.
            </div>
          </Tooltip>
        }
      >
        <span>
          <Icon id="tool-tip" label="tooltip" />
        </span>
      </OverlayTrigger>
    );

    return (
      <Authorization action="manage" subject={BUILDER} eventKey="domains">
        {authorized => (authorized ? (
          <form onSubmit={this.handleSubmit} className={style.root}>
            <AsyncFeedback
              isSaving={isSaving}
              success="Your changes have been saved."
              error={error}
            />
            <Form.Group controlId="primary" className={style.heading}>
              <Form.Label required>Primary Domain</Form.Label>
              <span className={style.tooltipContainer}>{tooltip}</span>
              <Form.Control
                name="primary"
                value={hostnames[0].hostname}
                onChange={this.handleChange(hostnames[0].id)}
              />
            </Form.Group>
            {hostnames.length > 1 ?
              <Form.Label required>Secondary Domains</Form.Label>
              : false
            }
            {hostnames.slice(1).map(({ id, hostname }) => (
              <InputGroup key={`secondary-field-${id}`} className={style.secondaryField}>
                <Form.Control
                  name={`hostname-${id}`}
                  value={hostname}
                  onChange={this.handleChange(id)}
                />
                <InputGroup.Button>
                  <CanaveralButton
                    className={style.deleteBtn}
                    context="danger"
                    onClick={this.handleDelete(id)}
                    icon="exit"
                    label="Delete"
                  />
                </InputGroup.Button>
              </InputGroup>
            ))}
            <CanaveralButton
              type="button"
              onClick={this.handleNewSecondary}
              className={style.addSecondaryBtn}
              icon="add"
              context="info"
            >
              Add Secondary Domain
            </CanaveralButton>
            <br />
            <Button
              type="submit"
              className={style.button}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </form>
        ) : <UnauthorizedBanner />)}
      </Authorization>
    );
  }
}
